import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { Briefing } from "../components/MemoryBriefing";
import { MemoryGraph } from "../components/MemoryGraph";
import { Button, Card, ErrorNote, Spinner } from "../components/primitives";
import { api } from "../lib/api";

/**
 * What the knowledge graph holds on a topic, written up (#135).
 *
 * Every signed-in user's page. The summary reads as the person asking: what
 * engagements shared — memory crosses projects by default — and their own
 * private material, never another user's private engagements or documents;
 * and a reference names the engagement behind it only where they may open it. One model call, at most the ceiling `/api/options` publishes,
 * charged to no credit; so the ceiling is stated beside the button rather
 * than copied here (#124).
 *
 * **Two tabs**: the summary, and the graph drawn — the company network and how
 * corroborated each engagement is, free and read the same way. The tab is the
 * `?tab=` in the URL, as on a run, so it survives a reload and a shared link.
 */
const TABS = ["summary", "graph"] as const;
type Tab = (typeof TABS)[number];

export function Memory() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "graph" ? "graph" : "summary";
  const setTab = (next: Tab) =>
    setParams(next === "summary" ? {} : { tab: next }, { replace: true });

  return (
    <div className="max-w-read space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Memory</h1>
        <p className="mt-1 text-sm text-ink-2">
          What earlier engagements and documents have taught the knowledge graph
          — written up as a briefing on a topic, or drawn as the companies it
          knows and how well each engagement is corroborated. It reads what
          engagements share and your own private material — never another
          user&apos;s private engagements or documents.
        </p>
      </div>
      <div role="tablist" aria-label="Memory" className="flex gap-1 border-b border-line">
        {TABS.map((name) => (
          <button
            key={name}
            id={`memory-tab-${name}`}
            role="tab"
            type="button"
            aria-selected={tab === name}
            aria-controls="memory-tabpanel"
            tabIndex={tab === name ? 0 : -1}
            onClick={() => setTab(name)}
            onKeyDown={(event) => {
              if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
              event.preventDefault();
              const next = TABS[(TABS.indexOf(tab) + 1) % TABS.length] ?? "summary";
              setTab(next);
              document.getElementById(`memory-tab-${next}`)?.focus();
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm capitalize transition-colors ${
              tab === name
                ? "border-accent font-medium text-ink"
                : "border-transparent text-ink-2 hover:text-ink"
            }`}
          >
            {name}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="memory-tabpanel" aria-labelledby={`memory-tab-${tab}`}>
        {tab === "summary" ? <Summary /> : <MemoryGraph />}
      </div>
    </div>
  );
}

function Summary() {
  const [question, setQuestion] = useState("");
  const summarise = useMutation({ mutationFn: () => api.memorySummary(question) });
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const ceiling = options.data?.actions.memory_summary?.ceiling_usd;

  return (
    <div className="space-y-4">
      <Card title="Summarize from memory">
        <div className="space-y-3 px-4 py-3">
          <textarea
            className="w-full rounded-sm border border-line bg-transparent px-2 py-1 text-sm"
            rows={2}
            placeholder="e.g. what do we know about heat sink suppliers in Germany?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={!question.trim() || summarise.isPending}
              onClick={() => summarise.mutate()}
            >
              {summarise.isPending ? "Summarising…" : "Summarize from memory"}
            </Button>
            <span className="text-xs text-ink-2">
              One model call
              {ceiling != null ? `, at most $${ceiling.toFixed(2)}` : ""}, not
              charged to your credit.
            </span>
          </div>
          {summarise.error && <ErrorNote error={summarise.error} />}
        </div>
      </Card>

      {summarise.isPending && (
        <Card title="Summary from memory">
          <Spinner label="Reading what memory holds and writing it up…" />
        </Card>
      )}
      {summarise.data && !summarise.isPending && (
        <Briefing summary={summarise.data} inGraph={me.data?.role === "admin"} />
      )}
    </div>
  );
}
